import React from 'react';
import { Link } from 'react-router-dom';
import { Compass, Sparkles, Zap } from 'lucide-react';

const FooterLink = ({ to, children }) => (
  <li>
    <Link to={to} className="btn-secondary footer-nav-link">
      {children}
    </Link>
  </li>
);

const FooterSection = ({ title, children }) => (
  <div>
    <h4 className="text-white font-semibold mb-4">{title}</h4>
    <ul className="space-y-2 text-sm text-text-secondary">
      {children}
    </ul>
  </div>
);

const Footer = () => {
  return (
    <footer className="bg-surface border-t border-white/10 py-12" data-testid="main-footer">
      <div className="container mx-auto px-6">
        {/* Buying signal banner */}
        <div className="prime-glow-card p-4 mb-6 relative overflow-hidden" data-testid="footer-stat-prime">
          <div className="absolute inset-0 opacity-25 pointer-events-none">
            <img
              src="https://images.unsplash.com/photo-1664575602554-2087b04935a5?auto=format&fit=crop&w=1400&q=80"
              alt="Data-backed buying signal"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative z-10">
            <p className="text-xs uppercase tracking-[0.16em] font-mono text-cyber-yellow mb-1 inline-flex items-center gap-2">
              <Compass size={13} /> Buying reality
            </p>
            <p className="text-sm text-text-secondary">Teams that define workflow outcomes first typically reduce tool waste by 18-35%.</p>
          </div>
        </div>

        {/* Main link grid - 5 columns */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand */}
          <div className="col-span-2 md:col-span-1">
            <h3 className="text-cyber-yellow font-black text-xl mb-4" style={{ fontFamily: 'Unbounded, sans-serif' }}>
              WOWROLLA
            </h3>
            <p className="text-text-secondary text-sm mb-4">
              Decision-first AI tool guidance for teams that want less noise and better outcomes.
            </p>
            <p className="text-text-secondary text-xs">
              We earn affiliate commissions on some tool links — always disclosed per the Competition Bureau of Canada.
              See{' '}
              <Link to="/transparency" className="footer-inline-link">
                our transparency page
              </Link>.
            </p>
            <p className="text-text-secondary text-xs mt-2">
              🇨🇦 Ottawa, Ontario, Canada. PIPEDA compliant.
            </p>
          </div>

          {/* Discover */}
          <FooterSection title="Discover">
            <FooterLink to="/find">AI Tool Finder</FooterLink>
            <FooterLink to="/categories">Browse Categories</FooterLink>
            <FooterLink to="/compare">Compare Tools</FooterLink>
            <FooterLink to="/best">Top Picks</FooterLink>
            <FooterLink to="/search">Search Tools</FooterLink>
            <FooterLink to="/submit-tool">Submit a Tool</FooterLink>
          </FooterSection>

          {/* Consulting */}
          <FooterSection title="Consulting">
            <FooterLink to="/consulting#start">Book a Discovery Call</FooterLink>
            <FooterLink to="/consulting#my-flow">My AI Flow</FooterLink>
            <FooterLink to="/consulting#roi">AI Readiness Studio</FooterLink>
            <FooterLink to="/consulting#packages">Packages and Pricing</FooterLink>
            <FooterLink to="/reviews">Tool Reviews</FooterLink>
            <FooterLink to="/faq">FAQ</FooterLink>
          </FooterSection>

          {/* Resources */}
          <FooterSection title="Resources">
            <FooterLink to="/newsletter">Newsletter</FooterLink>
            <FooterLink to="/blog">Blog</FooterLink>
            <FooterLink to="/guides">Guides</FooterLink>
            <FooterLink to="/transparency">Transparency</FooterLink>
            <FooterLink to="/about">About Us</FooterLink>
            <FooterLink to="/company">Company</FooterLink>
            <FooterLink to="/contact">Contact</FooterLink>
          </FooterSection>

          {/* Legal */}
          <FooterSection title="Legal">
            <FooterLink to="/privacy-policy">Privacy Policy</FooterLink>
            <FooterLink to="/terms-of-service">Terms of Service</FooterLink>
            <FooterLink to="/disclaimer">Disclaimer</FooterLink>
            <FooterLink to="/transparency">Affiliate Disclosure</FooterLink>
            <FooterLink to="/sitemap">Sitemap</FooterLink>
          </FooterSection>
        </div>

        {/* Micro CTAs */}
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="footer-micro-ctas">
          <Link to="/find" className="prime-glow-card p-3 text-sm text-text-secondary hover:border-cyber-yellow/40 inline-flex items-center gap-2 transition-all">
            <Sparkles size={14} className="text-cyber-yellow" />
            Not sure what to buy? Start with the finder.
          </Link>
          <Link to="/consulting#start" className="prime-glow-card p-3 text-sm text-text-secondary hover:border-cyber-yellow/40 inline-flex items-center gap-2 transition-all">
            <Zap size={14} className="text-laser-green" />
            Need certainty? Book consulting.
          </Link>
          <Link to="/newsletter" className="prime-glow-card p-3 text-sm text-text-secondary hover:border-cyber-yellow/40 inline-flex items-center gap-2 transition-all">
            <Compass size={14} className="text-cyber-yellow" />
            Get weekly buyer intelligence.
          </Link>
        </div>

        <div className="mt-3">
          <Link to="/faq" className="prime-glow-card p-3 text-sm text-text-secondary hover:border-cyber-yellow/40 inline-flex items-center gap-2 transition-all w-full justify-center">
            <Compass size={14} className="text-cyber-yellow" />
            Have questions? Read the full FAQ.
          </Link>
        </div>

        {/* Copyright */}
        <div className="mt-12 pt-8 border-t border-white/10 text-center text-sm text-text-secondary">
          <p>&copy; {new Date().getFullYear()} Wowrolla. All rights reserved.</p>
          <p className="mt-2">Newsletter: one practical buyer brief per week, not generic hype.</p>
          <p className="mt-3 text-xs" style={{ color: 'var(--text-muted)' }}>
            <strong>Affiliate disclosure:</strong> Some links on this site are affiliate links. We earn a commission if you
            click and make a purchase, at no extra cost to you. This is a material connection disclosed in accordance with
            the Competition Bureau of Canada's guidelines.{' '}
            <Link to="/transparency" className="footer-inline-link">See all rates</Link>
          </p>
          <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
            Data hosted in Canada by a third-party cloud provider (US CLOUD Act may apply). See our{' '}
            <Link to="/privacy-policy" className="footer-inline-link">Privacy Policy</Link>{' '}
            for full PIPEDA &amp; CLOUD Act disclosure.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;

